"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";

export default function Content3({ data }: any) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkIsDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);

    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current
        .play()
        .catch((e) => console.warn("Autoplay prevented:", e));
    }

    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  //   if (!data?.isLive) return null;

  return (
    <section className="relative w-full overflow-hidden bg-black">
      {/* ✅ Fullscreen video */}
      <div className={`${isDesktop ? "h-screen" : "h-[90vh]"} w-full relative`}>
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted={isMuted}
          playsInline
          preload="metadata"
          poster="/placeholder.jpg"
        >
          <source src={data?.title} type="video/mp4" />
        </video>

        {/* ✅ Overlay gradient + content */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-black/10"></div>

          {/* Centered content */}
          <div className="relative text-center text-white px-6 container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="max-w-xl mx-auto"
            >
              {data?.superScriptText && (
                <Link href={data?.subScriptText || "#"} passHref>
                  <button className="group relative px-14 py-4 bg-transparent text-white rounded-sm font-medium transition-all duration-700 text-lg overflow-hidden border border-white/40 hover:border-white/80">
                    {/* Background layer shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                    {/* Animated border */}
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="absolute top-0 left-0 w-0 h-px bg-white group-hover:w-full transition-all duration-500 delay-100"></div>
                      <div className="absolute top-0 right-0 h-0 w-px bg-white group-hover:h-full transition-all duration-500 delay-200"></div>
                      <div className="absolute bottom-0 right-0 w-0 h-px bg-white group-hover:w-full transition-all duration-500 delay-300"></div>
                      <div className="absolute bottom-0 left-0 h-0 w-px bg-white group-hover:h-full transition-all duration-500 delay-400"></div>
                    </div>

                    {/* Shimmer strip */}
                    <div className="absolute inset-0 -inset-x-32 -inset-y-10 bg-gradient-to-r from-transparent via-white/15 to-transparent transform rotate-45 translate-y-32 group-hover:translate-y-[-100%] transition-transform duration-1000"></div>

                    {/* Button content */}
                    <span className="relative z-10 uppercase tracking-widest text-sm font-light flex items-center justify-center">
                      {data.superScriptText}
                      <svg
                        className="w-4 h-4 ml-4 transition-all duration-500 group-hover:translate-x-1 group-hover:scale-110"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </span>

                    {/* Pulsing outline effect */}
                    <motion.span
                      className="absolute inset-0 border border-white/30"
                      animate={{
                        boxShadow: [
                          "0 0 0px rgba(255,255,255,0.3)",
                          "0 0 10px rgba(255,255,255,0.5)",
                          "0 0 0px rgba(255,255,255,0.3)",
                        ],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatDelay: 2,
                      }}
                    />
                  </button>
                </Link>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
